// src/Components/FilteredProductList.js
import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase/firestore';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import CategoryTabs from './CategoryTabs';
import ProductCard from './ProductCard';
import { FaSearch, FaSlidersH, FaTimes } from 'react-icons/fa';
import CommunitySlider from '../Components/Slides';

const FilteredProductList = () => {
  const [products, setProducts] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('Все');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  
  // Состояния для фильтра
  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const [sortBy, setSortBy] = useState('displayOrder');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [onlyDiscounted, setOnlyDiscounted] = useState(false);
  const [priceRange, setPriceRange] = useState({ min: 0, max: 100000 });
  const [availableCategories, setAvailableCategories] = useState([]);
  const [allCategoriesFromDB, setAllCategoriesFromDB] = useState([]);

  // Загрузка категорий из коллекции categories
  useEffect(() => {
    const fetchCategoriesFromDB = async () => {
      try {
        const q = query(collection(db, 'categories'), orderBy('order', 'asc'));
        const snapshot = await getDocs(q);
        const cats = snapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name,
          order: doc.data().order || 9999,
        }));
        
        // Добавляем категорию "Все" в начало
        const allCategories = [
          { id: 'all', name: 'Все', order: -1 },
          ...cats
        ];
        
        setAllCategoriesFromDB(allCategories);
        setAvailableCategories(cats.map(cat => cat.name));
      } catch (err) {
        console.error("Error loading categories from DB:", err);
        setAllCategoriesFromDB([{ id: 'all', name: 'Все', order: -1 }]);
      }
    };

    fetchCategoriesFromDB();
  }, []);

  // Загрузка товаров с учетом displayOrder
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        // Загружаем товары, отсортированные по displayOrder
        const q = query(
          collection(db, 'products'), 
          orderBy('displayOrder', 'asc')
        );
        
        const snapshot = await getDocs(q);
        const items = snapshot.docs
          .map((doc) => {
            const data = doc.data();
            if (!data) return null;
            
            const displayOrder = data.displayOrder !== undefined 
              ? data.displayOrder 
              : 9999;
            
            // Преобразуем категории в массив объектов
            let categories = [];
            if (Array.isArray(data.categories)) {
              categories = data.categories.map(cat => {
                if (typeof cat === 'object' && cat !== null) {
                  return {
                    name: cat.name || String(cat),
                    order: cat.order || 9999
                  };
                } else {
                  return {
                    name: String(cat),
                    order: 9999
                  };
                }
              });
            } else if (data.categories && typeof data.categories === 'object') {
              categories = [{
                name: data.categories.name || 'Без категории',
                order: data.categories.order || 9999
              }];
            } else if (data.categories) {
              categories = [{
                name: String(data.categories),
                order: 9999
              }];
            }
            
            // Получаем первую категорию для группировки
            const firstCategory = categories.length > 0 
              ? categories[0].name 
              : 'Без категории';
            
            return { 
              id: doc.id, 
              ...data,
              displayOrder,
              categories,
              price: Number(data.price) || 0,
              discount: Number(data.discount) || 0,
              rating: Number(data.rating) || 0,
              firstCategory,
              // Сохраняем оригинальный displayOrder для сортировки внутри категории
              originalDisplayOrder: data.displayOrder !== undefined 
                ? data.displayOrder 
                : 9999
            };
          })
          .filter(Boolean);
        
        // Сортируем по displayOrder для глобального порядка
        items.sort((a, b) => a.displayOrder - b.displayOrder);
        
        setProducts(items);
        
        if (items.length > 0) {
          const maxPrice = Math.max(...items.map(p => p.price));
          setPriceRange(prev => ({ ...prev, max: Math.ceil(maxPrice) }));
        }
      } catch (err) {
        console.error("Error loading products:", err);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Применение фильтров
  useEffect(() => {
    let result = [...products];
    
    // Фильтр по выбранной категории (из табов)
    if (selectedCategory !== 'Все') {
      result = result.filter(p => {
        return p.categories.some(cat => 
          cat.name === selectedCategory
        );
      });
    }

    // Фильтр по поиску
    if (searchTerm.trim() !== '') {
      const term = searchTerm.trim().toLowerCase();
      result = result.filter(p =>
        p.title.toLowerCase().includes(term) ||
        (p.description && p.description.toLowerCase().includes(term))
      );
    }

    // Дополнительные фильтры из попапа
    if (selectedCategories.length > 0) {
      result = result.filter(p => {
        return p.categories.some(cat => 
          selectedCategories.includes(cat.name)
        );
      });
    }

    // Фильтр по цене
    result = result.filter(p => {
      const price = p.discount ? p.price * (100 - p.discount) / 100 : p.price;
      return price >= priceRange.min && price <= priceRange.max;
    });

    // Фильтр по скидке
    if (onlyDiscounted) {
      result = result.filter(p => p.discount > 0);
    }

    // Сортировка - ВАЖНО: сохраняем глобальный displayOrder как приоритет
    result.sort((a, b) => {
      const priceA = a.discount ? a.price * (100 - a.discount) / 100 : a.price;
      const priceB = b.discount ? b.price * (100 - b.discount) / 100 : b.price;
      
      switch (sortBy) {
        case 'price_asc':
          return priceA - priceB;
        case 'price_desc':
          return priceB - priceA;
        case 'discount_desc':
          return (b.discount || 0) - (a.discount || 0);
        case 'rating_desc':
          return (b.rating || 0) - (a.rating || 0);
        case 'newest':
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        case 'displayOrder':
        default:
          // Сохраняем глобальный порядок displayOrder
          return a.originalDisplayOrder - b.originalDisplayOrder;
      }
    });

    setFiltered(result);
  }, [products, selectedCategory, searchTerm, sortBy, selectedCategories, onlyDiscounted, priceRange]);

  // Мемоизированная группировка товаров по категориям с правильным порядком
  const groupedProducts = useMemo(() => {
    if (selectedCategory !== 'Все') {
      return null;
    }

    const grouped = {};
    
    // Проходим по уже отсортированным товарам (по displayOrder)
    filtered.forEach(product => {
      const category = product.firstCategory;
      
      if (!grouped[category]) {
        grouped[category] = [];
      }
      
      grouped[category].push(product);
    });

    return grouped;
  }, [filtered, selectedCategory]);

  // Мемоизированные категории для отображения с правильным порядком товаров
  const categoriesForDisplay = useMemo(() => {
    if (!groupedProducts) return null;
    
    // Используем категории из DB (без "Все")
    const categoriesFromDB = allCategoriesFromDB.filter(cat => cat.name !== 'Все');
    
    // Создаем массив категорий с товарами
    const categoriesWithProducts = categoriesFromDB
      .filter(category => groupedProducts[category.name])
      .map(category => {
        // Получаем товары этой категории
        const categoryProducts = groupedProducts[category.name];
        
        // Сортируем товары внутри категории по их глобальному displayOrder
        const sortedProducts = [...categoryProducts].sort((a, b) => 
          a.originalDisplayOrder - b.originalDisplayOrder
        );
        
        return {
          ...category,
          products: sortedProducts
        };
      })
      // Сортируем категории по order из базы данных
      .sort((a, b) => a.order - b.order);
    
    // Добавляем категорию "Без категории" если есть такие товары
    if (groupedProducts['Без категории']) {
      const noCategoryProducts = [...groupedProducts['Без категории']]
        .sort((a, b) => a.originalDisplayOrder - b.originalDisplayOrder);
      
      categoriesWithProducts.push({
        id: 'no-category',
        name: 'Без категории',
        order: 10000, // В самый конец
        products: noCategoryProducts
      });
    }
    
    return categoriesWithProducts;
  }, [groupedProducts, allCategoriesFromDB]);

  // Мемоизированный список товаров для отображения в выбранной категории
  const productsForSelectedCategory = useMemo(() => {
    if (selectedCategory === 'Все') return null;
    
    // Возвращаем уже отфильтрованные и отсортированные товары
    return filtered;
  }, [filtered, selectedCategory]);

  // Обработчик выбора категории в фильтре
  const handleCategoryToggle = (category) => {
    setSelectedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  // Сброс фильтров
  const resetFilters = () => {
    setSortBy('displayOrder');
    setSelectedCategories([]);
    setOnlyDiscounted(false);
    setPriceRange({ min: 0, max: priceRange.max });
  };

  // Применение фильтров и закрытие попапа
  const applyFilters = () => {
    setShowFilterPopup(false);
  };

  // Получение текста для кнопки сортировки
  const getSortButtonText = () => {
    switch (sortBy) {
      case 'price_asc': return 'По возрастанию цены';
      case 'price_desc': return 'По убыванию цены';
      case 'discount_desc': return 'По размеру скидки';
      case 'rating_desc': return 'По рейтингу';
      case 'newest': return 'Сначала новые';
      case 'displayOrder': return 'По порядку на витрине';
      default: return 'Сортировать';
    }
  };

  return (
    <div className="pb-24 relative">
      {/* Search and Filter */}
      <div className="flex items-center gap-2 px-4 mt-2">
        <div className="flex items-center rounded-xl shadow p-6 flex-grow bg-[#F8F8F8]">
          <FaSearch className="text-gray-500 mr-2" />
          <input
            type="text"
            placeholder="Поиск товаров"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="outline-none w-full text-sm bg-transparent"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="ml-2 text-gray-400 hover:text-gray-600"
            >
              <FaTimes />
            </button>
          )}
        </div>
        <button 
          className="bg-cyan-500 hover:bg-cyan-600 text-white p-6 rounded-xl shadow transition-colors"
          onClick={() => setShowFilterPopup(true)}
        >
          <FaSlidersH />
        </button>
      </div>

      {/* Categories */}
      <CategoryTabs
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
      />

      {/* Slider */}
      <div className="grid gap-4 p-4 pt-6">
        <CommunitySlider />
      </div>

      {/* Информация о примененных фильтрах */}
      {(sortBy !== 'displayOrder' || selectedCategories.length > 0 || onlyDiscounted || 
        priceRange.min > 0 || priceRange.max < 100000) && (
        <div className="px-4 mt-2 mb-2">
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-sm text-gray-600">Применены фильтры:</span>
            {sortBy !== 'displayOrder' && (
              <span className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full">
                {getSortButtonText()}
              </span>
            )}
            {onlyDiscounted && (
              <span className="bg-red-100 text-red-700 text-xs px-2 py-1 rounded-full">
                Со скидкой
              </span>
            )}
            {(priceRange.min > 0 || priceRange.max < 100000) && (
              <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full">
                Цена: {priceRange.min}-{priceRange.max}₽
              </span>
            )}
            <button
              onClick={resetFilters}
              className="text-sm text-gray-500 hover:text-gray-700 underline ml-2"
            >
              Сбросить
            </button>
          </div>
        </div>
      )}

      {/* Product list */}
      {loading ? (
        <p className="text-center mt-8">Загрузка товаров...</p>
      ) : filtered.length === 0 ? (
        <div className="text-center mt-8 px-4">
          <p className="text-gray-600 mb-4">Нет подходящих товаров</p>
          <button
            onClick={resetFilters}
            className="bg-cyan-500 hover:bg-cyan-600 text-white px-4 py-2 rounded-lg"
          >
            Сбросить фильтры
          </button>
        </div>
      ) : (
        <div className="pb-8">
          {/* Если выбрана конкретная категория, показываем все товары */}
          {selectedCategory !== 'Все' ? (
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4 px-4 pt-4 auto-rows-fr">
              {productsForSelectedCategory?.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          ) : (
            // Если выбрана "Все", группируем по категориям
            categoriesForDisplay && categoriesForDisplay.map((categoryData) => (
              <div key={categoryData.id} className="mb-6">
                {/* Заголовок категории с серой полупрозрачной полоской */}
                <div className="relative mb-3">
                  <div className="absolute inset-x-0 h-10 bg-gray-100/50 rounded-lg -top-1"></div>
                  <div className="relative px-4 py-2">
                    <h2 className="text-lg font-bold text-[#0891B2]">
                      {categoryData.name}
                    </h2>
                  </div>
                </div>
                
                {/* Товары этой категории - уже отсортированные по displayOrder */}
                <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4 px-4 auto-rows-fr">
                  {categoryData.products.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Popup фильтрации */}
      {showFilterPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl animate-slideUp">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center z-10">
              <h3 className="text-xl font-bold">Фильтры</h3>
              <button
                onClick={() => setShowFilterPopup(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Сортировка */}
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-800">Сортировка</h4>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'displayOrder', label: 'Порядок на витрине' },
                    { value: 'price_asc', label: 'Дешевле' },
                    { value: 'price_desc', label: 'Дороже' },
                    { value: 'discount_desc', label: 'Большая скидка' },
                    { value: 'rating_desc', label: 'Высокий рейтинг' },
                    { value: 'newest', label: 'Новинки' }
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setSortBy(option.value)}
                      className={`py-3 px-4 rounded-lg text-sm font-medium transition-colors ${
                        sortBy === option.value
                          ? 'bg-cyan-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Категории */}
              {availableCategories.length > 0 && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-800">Категории</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {availableCategories.map((category) => (
                      <button
                        key={category}
                        onClick={() => handleCategoryToggle(category)}
                        className={`py-3 px-4 rounded-lg text-sm font-medium transition-colors ${
                          selectedCategories.includes(category)
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Ценовой диапазон */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <h4 className="font-semibold text-gray-800">Цена</h4>
                  <span className="text-sm text-gray-600">
                    {priceRange.min}₽ - {priceRange.max}₽
                  </span>
                </div>
                <div className="px-2">
                  <div className="relative h-2 bg-gray-200 rounded-full">
                    <div 
                      className="absolute h-full bg-cyan-500 rounded-full"
                      style={{
                        left: `${(priceRange.min / priceRange.max) * 100}%`,
                        right: `${100 - (priceRange.max / priceRange.max) * 100}%`
                      }}
                    />
                    <input
                      type="range"
                      min="0"
                      max={priceRange.max}
                      value={priceRange.min}
                      onChange={(e) => setPriceRange(prev => ({ ...prev, min: Number(e.target.value) }))}
                      className="absolute w-full h-full opacity-0 cursor-pointer"
                    />
                    <input
                      type="range"
                      min="0"
                      max={priceRange.max}
                      value={priceRange.max}
                      onChange={(e) => setPriceRange(prev => ({ ...prev, max: Number(e.target.value) }))}
                      className="absolute w-full h-full opacity-0 cursor-pointer"
                    />
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mt-2">
                    <span>0₽</span>
                    <span>{priceRange.max}₽</span>
                  </div>
                </div>
              </div>

              {/* Скидка */}
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-800">Скидка</h4>
                <label className="flex items-center gap-3 cursor-pointer">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={onlyDiscounted}
                      onChange={(e) => setOnlyDiscounted(e.target.checked)}
                      className="sr-only"
                    />
                    <div className={`w-10 h-6 rounded-full transition-colors ${
                      onlyDiscounted ? 'bg-green-500' : 'bg-gray-300'
                    }`}>
                      <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                        onlyDiscounted ? 'transform translate-x-5' : 'transform translate-x-1'
                      }`} />
                    </div>
                  </div>
                  <span className="text-gray-700">Только товары со скидкой</span>
                </label>
              </div>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex gap-3">
              <button
                onClick={resetFilters}
                className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                Сбросить
              </button>
              <button
                onClick={applyFilters}
                className="flex-1 py-3 px-4 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg font-medium transition-colors"
              >
                Применить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilteredProductList;